import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../authContext';
import {
  ApplicationButton,
  ApplicationStateRegion,
} from '../../components/application';
import { acceptCurrentTerms } from './termsAcceptanceApi';

const DeleteAccountModal = lazy(() => import('../../components/modals/app-modals/DeleteAccountModal'));

const TermsAcceptanceGate = ({ children, reloadPage = () => window.location.reload() }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const headingRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const [actionState, setActionState] = useState('idle');
  const [message, setMessage] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const status = auth.user?.termsAcceptance;
  const blocksAccess = !status || (
    status.enforcementRequired && status.status !== 'current'
  );

  useEffect(() => {
    if (blocksAccess) headingRef.current?.focus();
  }, [blocksAccess]);

  if (!blocksAccess) return children;

  const logout = () => { auth.logout(); navigate('/'); };

  if (!status) {
    return (
      <main className="auth-page terms-acceptance-page" aria-labelledby="terms-recovery-title">
        <section className="auth-card auth-card--wide terms-acceptance-card">
          <h1 id="terms-recovery-title">
            {t('post_registration_recovery_title', 'Almost there')}
          </h1>
          <div className="terms-acceptance-card__content">
            <p>
              {t('post_registration_recovery_body', 'Your account has been created. Refresh the page to continue.')}
            </p>
          </div>
          <div className="terms-acceptance-card__actions">
            <ApplicationButton className="terms-acceptance-card__primary-action" onClick={reloadPage}>
              {t('refresh_page', 'Refresh page')}
            </ApplicationButton>
            <div className="terms-acceptance-card__secondary-actions">
              <ApplicationButton onClick={logout} variant="secondary">
                {t('logout', 'Log out')}
              </ApplicationButton>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const retryUser = async () => {
    setActionState('refreshing');
    const refreshed = await auth.fetchUserData(auth.user?.token);
    if (!refreshed) setMessage(t('terms_refresh_failed', 'We could not refresh your account. Please try again.'));
    setActionState('idle');
  };

  const accept = async () => {
    if (!status?.publishedDocument || !status?.currentRequirementId) return;
    setActionState('accepting');
    setMessage('');
    try {
      await acceptCurrentTerms({
        apiUrl,
        token: auth.user.token,
        metadata: {
          publishedDocument: status.publishedDocument,
          acceptanceRequirement: { id: status.currentRequirementId },
        },
      });
      const refreshed = await auth.fetchUserData(auth.user.token);
      if (!refreshed) setMessage(t('terms_refresh_failed', 'Acceptance was saved, but your account could not be refreshed. Please retry.'));
    } catch (error) {
      setMessage(error.response?.data?.error === 'TERMS_ACCEPTANCE_STALE'
        ? t('terms_acceptance_stale', 'The Terms changed while this page was open. Refresh and review the current Terms.')
        : t('terms_acceptance_failed', 'We could not record your acceptance. Please try again.'));
    } finally {
      setActionState('idle');
    }
  };

  const confirmDelete = async () => {
    setActionState('deleting');
    setMessage('');
    try {
      const { deleteCurrentAccount } = await import('../user/deleteCurrentAccount');
      await deleteCurrentAccount({ apiUrl, token: auth.user.token });
      auth.logout();
      navigate('/');
    } catch (_error) {
      setMessage(t('account_delete_error', 'There was an error deleting your account.'));
      setActionState('idle');
    }
  };

  return (
    <main className="auth-page terms-acceptance-page" aria-labelledby="terms-gate-title">
      <section className="auth-card auth-card--wide terms-acceptance-card" data-testid="terms-acceptance-card">
        <h1 id="terms-gate-title" ref={headingRef} tabIndex="-1">
          {t('terms_acceptance_required_title', 'Terms & Conditions acceptance required')}
        </h1>
        <div className="terms-acceptance-card__content">
          <p>{t('terms_acceptance_required_text', 'Review and explicitly accept the current Terms & Conditions to continue.')}</p>
          <p className="terms-acceptance-card__terms-link">
            <a href="/terms-and-conditions" target="_blank" rel="noreferrer">
              {t('view_terms_and_conditions', 'View Terms & Conditions')}
            </a>
          </p>
        </div>
        {message && <ApplicationStateRegion message={message} status="error" />}
        <div className="terms-acceptance-card__actions">
          {status?.status === 'pending' && status?.publishedDocument && (
            <ApplicationButton className="terms-acceptance-card__primary-action" disabled={actionState !== 'idle'} onClick={accept}>
              {actionState === 'accepting' ? t('saving', 'Saving...') : t('accept_and_continue', 'Accept and continue')}
            </ApplicationButton>
          )}
          <div className="terms-acceptance-card__secondary-actions">
            {status?.status === 'malformed' && (
              <button className="web-modal-button--link" disabled={actionState !== 'idle'} onClick={retryUser} type="button">
                {t('retry', 'Retry')}
              </button>
            )}
            <button className="web-modal-button--link" disabled={actionState !== 'idle'} onClick={logout} type="button">
              {t('logout', 'Log out')}
            </button>
          </div>
          <button
            className="terms-acceptance-card__delete-action web-modal-button--link"
            disabled={actionState !== 'idle'}
            onClick={() => setShowDelete(true)}
            type="button"
          >
            {t('delete_account', 'Delete account')}
          </button>
        </div>
      </section>
      {showDelete && (
        <Suspense fallback={null}>
          <DeleteAccountModal
            error={message}
            isOpen
            onCancel={() => setShowDelete(false)}
            onConfirm={confirmDelete}
            pending={actionState === 'deleting'}
          />
        </Suspense>
      )}
    </main>
  );
};

export default TermsAcceptanceGate;
